import { call, put, takeLatest } from "redux-saga/effects";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";

import {
    EXPORT_PRE_EMBAUCHE_DOCUMENT_PDF_REQUEST,
    exportPreEmbaucheDocumentPdfFail,
    exportPreEmbaucheDocumentPdfSuccess,
    FETCH_PRE_EMBAUCHE_DOCUMENT_REQUEST,
    fetchPreEmbaucheDocumentFail,
    fetchPreEmbaucheDocumentSuccess,
    GENERATE_PRE_EMBAUCHE_DOCUMENT_REQUEST,
    generatePreEmbaucheDocumentFail,
    generatePreEmbaucheDocumentSuccess,
    setPreEmbaucheError,
    setPreEmbaucheLoading,
    UPDATE_PRE_EMBAUCHE_DOCUMENT_REQUEST,
    updatePreEmbaucheDocumentFail,
    updatePreEmbaucheDocumentSuccess,
    UPDATE_PRE_EMBAUCHE_PROCEDURE_STEP_REQUEST,
    updatePreEmbaucheProcedureStepFail,
    updatePreEmbaucheProcedureStepSuccess,
} from "../actions/preEmbaucheActions";
import { API_BASE_URL, extractErrorMessage, getRequest, getRequestForPdf, postRequest, putRequest } from "../../../api";

const getUid = () => sessionStorage.getItem("uid");
const getToken = () => sessionStorage.getItem("token");

const DOCUMENT_CONFIG = {
    lettreConfirmation: {
        segment: "lettre-confirmation",
        label: "Lettre de confirmation",
        filenamePrefix: "Lettre_confirmation",
    },
    contratTravail: {
        segment: "contrat-travail",
        label: "Contrat de travail",
        filenamePrefix: "Contrat_travail",
    },
    procedureIntegration: {
        segment: "procedure-integration",
        label: "Procedure_integration",
        filenamePrefix: "Procedure_integration",
    },
};

const buildLoadingKey = (documentType, candidatureId, operation) =>
    `${documentType}:${candidatureId}:${operation}`;

const isMockableStatus = (status) => [404, 405, 409, 500, 501].includes(Number(status));

const cloneDocument = (document, overrides = {}) => ({
    ...(document || {}),
    ...overrides,
});

const ensureFilename = (documentType, candidatureId) => {
    const config = DOCUMENT_CONFIG[documentType];
    return `${config?.filenamePrefix || "document"}_${candidatureId}.pdf`;
};

const extractFilename = (contentDisposition, fallback) => {
    if (!contentDisposition) return fallback;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

    const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return basicMatch?.[1] || fallback;
};

const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const writeWrappedText = (pdf, text, x, startY, maxWidth, lineHeight = 7) => {
    const lines = pdf.splitTextToSize(text || "", maxWidth);
    pdf.text(lines, x, startY);
    return startY + lines.length * lineHeight;
};

const getImageFormat = (dataUrl) => {
    if (String(dataUrl).startsWith("data:image/png")) return "PNG";
    if (String(dataUrl).startsWith("data:image/webp")) return "WEBP";
    return "JPEG";
};

const loadImageAsDataUrl = async (url) => {
    if (!url) return null;
    if (String(url).startsWith("data:image/")) return url;

    try {
        const absoluteUrl = new URL(url, window.location.origin).toString();
        const response = await fetch(absoluteUrl);
        if (!response.ok) return null;

        const blob = await response.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        return null;
    }
};

const drawDocumentLogo = (pdf, logoDataUrl) => {
    if (!logoDataUrl) return false;

    try {
        pdf.addImage(logoDataUrl, getImageFormat(logoDataUrl), 14, 10, 36, 18);
        return true;
    } catch (error) {
        return false;
    }
};

const writeContractText = (pdf, content, logoDataUrl) => {
    const marginX = 18;
    const maxWidth = 174;
    const pageHeight = pdf.internal.pageSize.getHeight();
    const bottomMargin = 18;
    const contractBlue = [35, 110, 165];
    const articleFill = [232, 242, 250];
    const hasLogo = drawDocumentLogo(pdf, logoDataUrl);
    let y = hasLogo ? 36 : 22;

    const ensureSpace = (height = 8) => {
        if (y + height > pageHeight - bottomMargin) {
            pdf.addPage();
            y = 22;
        }
    };

    const drawTitle = (lines) => {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(15);
        pdf.setTextColor(...contractBlue);
        pdf.text(lines, pdf.internal.pageSize.getWidth() / 2, y, { align: "center" });
        y += lines.length * 8;

        pdf.setDrawColor(...contractBlue);
        pdf.setLineWidth(0.6);
        pdf.line(marginX, y, pdf.internal.pageSize.getWidth() - marginX, y);
        y += 9;
    };

    const drawArticleTitle = (lines) => {
        const lineHeight = 6.5;
        const blockHeight = lines.length * lineHeight + 3;
        ensureSpace(blockHeight + 2);

        pdf.setFillColor(...articleFill);
        pdf.rect(marginX - 2, y - 5, maxWidth + 4, blockHeight, "F");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11.5);
        pdf.setTextColor(...contractBlue);
        pdf.text(lines, marginX, y);
        y += blockHeight + 2;
    };

    String(content || "")
        .split("\n")
        .forEach((rawLine, index) => {
            const line = rawLine.trim();

            if (!line) {
                y += index === 0 ? 0 : 4;
                return;
            }

            const isTitle = index === 0;
            const isArticle = /^Article\s+\d+/i.test(line);

            const lines = pdf.splitTextToSize(line, maxWidth);

            if (isTitle) {
                ensureSpace(lines.length * 8 + 12);
                drawTitle(lines);
                return;
            }

            if (isArticle) {
                drawArticleTitle(lines);
                return;
            }

            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(10.5);
            pdf.setTextColor(30, 41, 59);

            const lineHeight = 6.5;
            ensureSpace(lines.length * lineHeight + 2);
            pdf.text(lines, marginX, y);
            y += lines.length * lineHeight;
        });

    pdf.setTextColor(0, 0, 0);
    pdf.setDrawColor(0, 0, 0);
};

const exportLetterPdfLocally = async (documentData, candidatureId) => {
    const pdf = new jsPDF();
    const logoDataUrl = await loadImageAsDataUrl(documentData.logoUrl);
    const hasLogo = drawDocumentLogo(pdf, logoDataUrl);
    const titleY = hasLogo ? 35 : 18;
    const cursorY = hasLogo ? 50 : 34;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text("Lettre de confirmation de recrutement", 14, titleY);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);

    writeWrappedText(pdf, documentData.content, 14, cursorY, 180);

    pdf.save(ensureFilename("lettreConfirmation", candidatureId));
};

const exportContractPdfLocally = async (documentData, candidatureId) => {
    const pdf = new jsPDF();
    const logoDataUrl = await loadImageAsDataUrl(documentData.logoUrl);
    writeContractText(pdf, documentData.contenu || "", logoDataUrl);
    pdf.save(ensureFilename("contratTravail", candidatureId));
};

const exportDocumentLocally = async (documentType, documentData, candidatureId) => {
    switch (documentType) {
        case "lettreConfirmation":
            await exportLetterPdfLocally(documentData, candidatureId);
            return;
        case "contratTravail":
            await exportContractPdfLocally(documentData, candidatureId);
            return;
        default:
            throw new Error("Type de document non pris en charge");
    }
};

export function* fetchPreEmbaucheDocumentSaga(action) {
    const { documentType, candidatureId } = action.payload;
    const config = DOCUMENT_CONFIG[documentType];
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey(documentType, candidatureId, "fetch");
    const url = `${API_BASE_URL}/api/pre-embauche/${candidatureId}/${config.segment}`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        const resp = yield call(getRequest, { url, uid, token });
        yield put(fetchPreEmbaucheDocumentSuccess(documentType, candidatureId, resp.data));
    } catch (error) {
        if (isMockableStatus(error?.response?.status)) {
            yield put(fetchPreEmbaucheDocumentSuccess(documentType, candidatureId, null));
        } else {
            const message = extractErrorMessage(error, `Erreur chargement ${config.label.toLowerCase()}`);
            yield put(fetchPreEmbaucheDocumentFail(documentType, candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export function* generatePreEmbaucheDocumentSaga(action) {
    const { documentType, candidatureId, document } = action.payload;
    const config = DOCUMENT_CONFIG[documentType];
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey(documentType, candidatureId, "generate");
    const url = `${API_BASE_URL}/api/pre-embauche/${candidatureId}/${config.segment}/generate`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        const resp = yield call(postRequest, { url, data: document, uid, token });
        yield put(generatePreEmbaucheDocumentSuccess(documentType, candidatureId, resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: `${config.label} generee`,
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        if (isMockableStatus(error?.response?.status)) {
            const mocked = cloneDocument(document, { isMocked: true, source: "mock" });
            yield put(generatePreEmbaucheDocumentSuccess(documentType, candidatureId, mocked));
            yield call([Swal, Swal.fire], {
                icon: "info",
                text: `${config.label} generee en mode brouillon local`,
                timer: 1800,
                showConfirmButton: false,
            });
        } else {
            const message = extractErrorMessage(error, `Erreur generation ${config.label.toLowerCase()}`);
            yield put(generatePreEmbaucheDocumentFail(documentType, candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
            yield call([Swal, Swal.fire], {
                icon: "error",
                text: message,
                timer: 2300,
                showConfirmButton: false,
            });
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export function* updatePreEmbaucheDocumentSaga(action) {
    const { documentType, candidatureId, document } = action.payload;
    const config = DOCUMENT_CONFIG[documentType];
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey(documentType, candidatureId, "save");
    const url = `${API_BASE_URL}/api/pre-embauche/${candidatureId}/${config.segment}`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        const resp = yield call(putRequest, { url, data: document, uid, token });
        yield put(updatePreEmbaucheDocumentSuccess(documentType, candidatureId, resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: `${config.label} enregistree`,
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        if (isMockableStatus(error?.response?.status)) {
            const mocked = cloneDocument(document, { isMocked: true, source: "mock" });
            yield put(updatePreEmbaucheDocumentSuccess(documentType, candidatureId, mocked));
            yield call([Swal, Swal.fire], {
                icon: "info",
                text: `${config.label} conservée `,
                timer: 1800,
                showConfirmButton: false,
            });
        } else {
            const message = extractErrorMessage(error, `Erreur enregistrement ${config.label.toLowerCase()}`);
            yield put(updatePreEmbaucheDocumentFail(documentType, candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
            yield call([Swal, Swal.fire], {
                icon: "error",
                text: message,
                timer: 2300,
                showConfirmButton: false,
            });
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export function* updatePreEmbaucheProcedureStepSaga(action) {
    const { candidatureId, stepId, step, document } = action.payload;
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey("procedureIntegration", candidatureId, "step");
    const url = `${API_BASE_URL}/api/pre-embauche/procedure-integration/steps/${stepId}`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        yield call(putRequest, { url, data: step, uid, token });
        yield put(updatePreEmbaucheProcedureStepSuccess(candidatureId, document));
    } catch (error) {
        if (isMockableStatus(error?.response?.status)) {
            yield put(updatePreEmbaucheProcedureStepSuccess(candidatureId, cloneDocument(document, { isMocked: true, source: "mock" })));
        } else {
            const message = extractErrorMessage(error, "Erreur mise a jour etape integration");
            yield put(updatePreEmbaucheProcedureStepFail(candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
            yield call([Swal, Swal.fire], {
                icon: "error",
                text: message,
                timer: 2300,
                showConfirmButton: false,
            });
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export function* exportPreEmbaucheDocumentPdfSaga(action) {
    const { documentType, candidatureId, document } = action.payload;
    const config = DOCUMENT_CONFIG[documentType];
    const token = getToken();
    const uid = getUid();
    const loadingKey = buildLoadingKey(documentType, candidatureId, "export");
    const url = `${API_BASE_URL}/api/pre-embauche/${candidatureId}/${config.segment}/pdf`;

    yield put(setPreEmbaucheLoading(loadingKey, true));
    yield put(setPreEmbaucheError(loadingKey, null));

    try {
        const resp = yield call(getRequestForPdf, {
            url,
            token,
            headers: { uid },
            responseType: "arraybuffer",
        });

        downloadBlob(
            resp.data,
            extractFilename(resp.headers?.["content-disposition"], ensureFilename(documentType, candidatureId))
        );

        yield put(exportPreEmbaucheDocumentPdfSuccess(documentType, candidatureId, document));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: `${config.label} exportee en PDF`,
            timer: 1800,
            showConfirmButton: false,
        });
    } catch (error) {
        try {
            yield call(exportDocumentLocally, documentType, document, candidatureId);
            yield put(exportPreEmbaucheDocumentPdfSuccess(documentType, candidatureId, cloneDocument(document, { isMocked: true, source: "mock" })));

            yield call([Swal, Swal.fire], {
                icon: "info",
                text: `${config.label} exportée  en PDF`,
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (localError) {
            const message = extractErrorMessage(error, `Erreur export PDF ${config.label.toLowerCase()}`);
            yield put(exportPreEmbaucheDocumentPdfFail(documentType, candidatureId, message));
            yield put(setPreEmbaucheError(loadingKey, message));
            yield call([Swal, Swal.fire], {
                icon: "error",
                text: message,
                timer: 2300,
                showConfirmButton: false,
            });
        }
    } finally {
        yield put(setPreEmbaucheLoading(loadingKey, false));
    }
}

export default function* preEmbaucheSaga() {
    yield takeLatest(FETCH_PRE_EMBAUCHE_DOCUMENT_REQUEST, fetchPreEmbaucheDocumentSaga);
    yield takeLatest(GENERATE_PRE_EMBAUCHE_DOCUMENT_REQUEST, generatePreEmbaucheDocumentSaga);
    yield takeLatest(UPDATE_PRE_EMBAUCHE_DOCUMENT_REQUEST, updatePreEmbaucheDocumentSaga);
    yield takeLatest(EXPORT_PRE_EMBAUCHE_DOCUMENT_PDF_REQUEST, exportPreEmbaucheDocumentPdfSaga);
    yield takeLatest(UPDATE_PRE_EMBAUCHE_PROCEDURE_STEP_REQUEST, updatePreEmbaucheProcedureStepSaga);
}
